import { useEffect, useState, useRef } from "react";
import Sidebar from "../components/Sidebar";
import { supabase } from "../lib/supabase";
import PostViewerModal from "../components/PostViewerModal";
import { useUser } from "../context/UserContext";
import "../styles/explore.css";

const PAGE_SIZE = 12;

function ExplorePage() {
    const [posts, setPosts] = useState([]);
    const [page, setPage] = useState(0);
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [selectedPost, setSelectedPost] = useState(null);
    const isFetchingRef = useRef(false);
    const { user } = useUser();

    const fetchPosts = async (currentPage) => {
        if (isFetchingRef.current || !hasMore) return;
        isFetchingRef.current = true;

        setLoading(true);

        const from = currentPage * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;

        const { data, error } = await supabase
            .from("posts")
            .select(`
            id,
            user_id,
            caption,
            created_at,
            post_images (
                id,
                image_path,
                order_index
            ),
            likes (
                user_id
            ),
                profiles!posts_user_profile_fkey(
                id,
                username,
                full_name,
                avatar_url,
                bio,
                country,
                created_at
            )
            `)
            .eq("profiles.is_private", false)
            .order("created_at", { ascending: false })
            .range(from, to);

        if (error) {
            console.log(error.message);
            setLoading(false);
            isFetchingRef.current = false;
            return;
        }

        if (!data || data.length === 0) {
            setHasMore(false);
            setLoading(false);
            isFetchingRef.current = false;
            return;
        }

        const formattedPosts = await Promise.all(
            data.map(async (post) => {
                const sortedImages = (post.post_images || []).sort(
                    (a, b) => a.order_index - b.order_index
                );

                const imagesWithSignedUrls = await Promise.all(
                    sortedImages.map(async (img) => {
                        const { data: signedData } = await supabase.storage
                            .from("posts")
                            .createSignedUrl(img.image_path, 3600);

                        return {
                            ...img,
                            signedUrl: signedData?.signedUrl || null,
                        };
                    })
                );

                return {
                    ...post,
                    post_images: imagesWithSignedUrls,
                    likesCount: post.likes?.length || 0,
                    isLiked: post.likes?.some((l) => l.user_id === user?.id) || false,
                };
            })
        );

        setPosts((prev) => [...prev, ...formattedPosts]);
        setPage(currentPage + 1);
        setLoading(false);
        isFetchingRef.current = false;
    };

    useEffect(() => {
    document.title = "Explore • CodeGram";
  }, []);

    useEffect(() => {
        if (!initialLoadDone) {
            fetchPosts(0);
            setInitialLoadDone(true);
        }
    }, [initialLoadDone]);

    useEffect(() => {
        const scrollContainer = document.querySelector(".profile-content");

        const handleScroll = () => {
            if (
                scrollContainer.scrollTop + scrollContainer.clientHeight >=
                scrollContainer.scrollHeight - 200
            ) {
                fetchPosts(page);
            }
        };

        scrollContainer?.addEventListener("scroll", handleScroll);

        return () =>
            scrollContainer?.removeEventListener("scroll", handleScroll);
    }, [page, hasMore]);

    return (
        <div className="profile-page">
            <Sidebar />

            <main className="profile-content">
                <div className="explore-container">
                    <div className="explore-grid">
                        {posts.map((post) => {
                            const firstImage = post.post_images?.[0];
                            if (!firstImage?.signedUrl) return null;

                            return (
                                <div
                                    key={post.id}
                                    className="explore-item"
                                    onClick={() => setSelectedPost(post)}
                                >
                                    <img src={firstImage.signedUrl} alt="post" />
                                </div>
                            );
                        })}
                    </div>

                    <PostViewerModal
                        open={!!selectedPost}
                        onClose={() => setSelectedPost(null)}
                        post={selectedPost}
                        posts={posts}
                        profile={selectedPost?.profiles}
                        onNextPost={() => {
                            const currentIndex = posts.findIndex(
                                (p) => p.id === selectedPost?.id
                            );
                            if (currentIndex < posts.length - 1) {
                                setSelectedPost(posts[currentIndex + 1]);
                            }
                        }}
                        onPrevPost={() => {
                            const currentIndex = posts.findIndex(
                                (p) => p.id === selectedPost?.id
                            );
                            if (currentIndex > 0) {
                                setSelectedPost(posts[currentIndex - 1]);
                            }
                        }}
                    />

                    {loading && <p className="loading">Loading...</p>}
                </div>
            </main>
        </div>
    );
}

export default ExplorePage;
